import { roundSahParticipantContribution } from './sah-contribution-rounding';
import {
  calculateSahContribution,
  mapDbServiceCategoryToCanonical,
  type SahMeansTier,
  type SahServiceCategory,
} from './sah';

/**
 * Shared pure SAH fee estimates use the canonical contribution engine for means
 * rates and one exact cost-times-rate helper for PostgreSQL numeric rounding.
 * Projections preserve the writer's guards and hardship precedence. Posting and
 * period caps remain outside the per-line projection; source parity of arithmetic
 * does not certify live billing or future funding schedules.
 */

const SLIDING_TIERS = new Set<SahMeansTier>(['PART_PENSIONER', 'CSHC_HOLDER']);

export function isSlidingMeansTier(tier: SahMeansTier): boolean {
  return SLIDING_TIERS.has(tier);
}

/** Format a 0-1 rate as a percentage string (0 decimals when whole). */
export function formatSahRate(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const pct = value * 100;
  return `${pct.toFixed(pct === Math.round(pct) ? 0 : 1)}%`;
}

export type SahCategoryEstimate = {
  /** Formatted percentage, or null when the SA assessed rate is required first. */
  percentLabel: string | null;
  rate: number | null;
  /** Engine-derived amount for a $100 service-cost example, when calculable. */
  clientContribution?: number;
  /** Engine-derived government-funded amount for a $100 service-cost example, when calculable. */
  governmentSubsidy?: number;
  needsAssessedRate: boolean;
  /** True when this category's rate is set by an active hardship determination. */
  hardshipApplied?: boolean;
};

/**
 * An active Services Australia hardship (Fee Reduction) determination for the
 * participant, in 0..100 percentage units — the same shape the engine helper
 * `sah_active_hardship_contribution_percent` resolves. FULL_WAIVER → 0%;
 * PARTIAL → the SA-assessed `reducedContributionPercent`.
 */
export type SahActiveHardship = {
  reductionKind: 'FULL_WAIVER' | 'PARTIAL';
  reducedContributionPercent: number | null;
  /** Inclusive tenant-local date window; omit for an already resolved determination. */
  effectiveFrom?: string;
  effectiveTo?: string | null;
};

/**
 * Pick the determination covering `today` from a list, mapping it to the
 * estimator's hardship shape. Window predicate is identical to the engine helper
 * `sah_active_hardship_contribution_percent` (effective_from <= today AND
 * effective_to null/>= today), regardless of status. The DB GiST no-overlap
 * exclusion guarantees at most one row covers today. Returns null when none does.
 */
export function activeHardshipForToday(
  determinations: ReadonlyArray<{
    reduction_kind: string;
    reduced_contribution_percent: number | null;
    effective_from: string;
    effective_to: string | null;
  }>,
  today: string,
): SahActiveHardship | null {
  const row = determinations.find(
    (d) => d.effective_from <= today && (!d.effective_to || d.effective_to >= today),
  );
  if (!row || !row.reduction_kind) return null;
  return {
    reductionKind: row.reduction_kind === 'FULL_WAIVER' ? 'FULL_WAIVER' : 'PARTIAL',
    reducedContributionPercent: row.reduced_contribution_percent,
  };
}

/** Resolve an optional inclusive window on the tenant-local service day.
 * A windowed determination with no service day is unresolved and never assumed.
 */
export function sahHardshipForDate(
  hardship: SahActiveHardship | null | undefined,
  serviceDate: string | null | undefined,
): SahActiveHardship | null {
  if (!hardship) return null;
  if (hardship.effectiveFrom || hardship.effectiveTo) {
    if (!serviceDate) return null;
    if (hardship.effectiveFrom && serviceDate < hardship.effectiveFrom) return null;
    if (hardship.effectiveTo && serviceDate > hardship.effectiveTo) return null;
  }
  return hardship;
}

/**
 * Resolve the hardship contribution rate as a 0..1 fraction, mirroring the DB
 * engine helper exactly: FULL_WAIVER → 0; PARTIAL → reducedContributionPercent/100.
 * Returns null when there is no usable hardship rate (no determination, or a
 * malformed partial without a rate — in which case the means resolver stands).
 */
function hardshipRate(hardship: SahActiveHardship | null | undefined): number | null {
  if (!hardship) return null;
  if (hardship.reductionKind === 'FULL_WAIVER') return 0;
  const p = hardship.reducedContributionPercent;
  if (p === null || p === undefined || !(p >= 0 && p <= 100)) return null;
  return p / 100;
}

function categoryEstimate(
  tier: SahMeansTier | null,
  category: SahServiceCategory,
  assessedRate: number | null | undefined,
  isGrandfathered: boolean,
  asOfDate?: string | null,
): SahCategoryEstimate {
  if (!tier) {
    const projection = projectLineContribution({ serviceCategory: category, billableAmount: 100 });
    return {
      percentLabel:
        projection.effectiveRate == null ? null : formatSahRate(projection.effectiveRate),
      rate: projection.effectiveRate,
      clientContribution: projection.clientContribution ?? undefined,
      governmentSubsidy: projection.governmentSubsidy ?? undefined,
      needsAssessedRate: projection.effectiveRate == null,
    };
  }
  try {
    const result = calculateSahContribution({
      serviceCategory: category,
      serviceCost: 100,
      meansTier: tier,
      individualAssessedRate: SLIDING_TIERS.has(tier) ? (assessedRate ?? undefined) : undefined,
      isGrandfathered,
      asOfDate,
    });
    return {
      percentLabel: formatSahRate(result.contributionRate),
      rate: result.contributionRate,
      clientContribution: result.clientContribution,
      governmentSubsidy: result.governmentSubsidy,
      needsAssessedRate: false,
    };
  } catch {
    // The engine throws for sliding tiers without an assessed rate — surface that
    // as "needs assessed rate" rather than ever showing a guessed number.
    return { percentLabel: null, rate: null, needsAssessedRate: true };
  }
}

export type SahFeeEstimate = {
  isSliding: boolean;
  /** Clinical care — engine-derived (the engine fixes this at 0% for every tier). */
  clinical: SahCategoryEstimate;
  independence: SahCategoryEstimate;
  everydayLiving: SahCategoryEstimate;
  /**
   * Set when an active hardship determination overrode the means resolver for the
   * non-clinical categories. `ratePercent` is the applied 0..100 rate (0 for a full
   * waiver) so callers can render "waived" vs "reduced to X%".
   */
  hardship: {
    applied: boolean;
    reductionKind: 'FULL_WAIVER' | 'PARTIAL';
    ratePercent: number;
  } | null;
};

/**
 * The My-Aged-Care-style fee estimate for a participant: given their pension
 * status (= means tier), the per-service-category participant-contribution
 * percentage (Clinical 0% / Independence / Everyday Living). For sliding-scale
 * tiers (part pensioner / CSHC holder) the Independence + Everyday figures are
 * the Services Australia individually-assessed rates — surfaced as
 * needs-assessed-rate until those are entered.
 */
export function estimateSahFees(input: {
  tier: SahMeansTier | null;
  asOfDate?: string | null;
  isGrandfathered: boolean;
  assessedIndependence?: number | null;
  assessedEverydayLiving?: number | null;
  /**
   * Active SA hardship (Fee Reduction) determination, if any. When present it
   * overrides the means-tier rate for the NON-CLINICAL categories only — exactly
   * mirroring `apply_sah_service_financials` (migration 20267940000000), where
   * clinical short-circuits to 0% BEFORE the hardship check and the hardship %
   * then replaces the inclusion/means rate for independence + everyday living.
   * Threading it here keeps the displayed estimate == the billed engine result.
   */
  activeHardship?: SahActiveHardship | null;
}): SahFeeEstimate {
  // Clinical is engine-derived, not hardcoded — the engine fixes it at 0% for
  // every tier; routing it through the engine keeps estimate == engine end-to-end.
  // It is ALSO unaffected by hardship (clinical short-circuits to 0 first).
  const clinical = categoryEstimate(
    input.tier,
    'CLINICAL',
    null,
    input.isGrandfathered,
    input.asOfDate,
  );

  const activeHardship = sahHardshipForDate(input.activeHardship, input.asOfDate);
  const hr = hardshipRate(activeHardship);
  if (hr !== null && activeHardship) {
    // Hardship override: both non-clinical categories take the determined rate,
    // and the sliding "needs assessed rate" state no longer applies (the override
    // is fully determined). round() to drop float noise from the /100.
    const ratePercent = Math.round(hr * 1000) / 10; // 0..100, one decimal
    const clientContribution = roundSahParticipantContribution(
      100,
      activeHardship.reductionKind === 'FULL_WAIVER'
        ? 0
        : activeHardship.reducedContributionPercent!,
    );
    const overridden: SahCategoryEstimate = {
      percentLabel: formatSahRate(hr),
      rate: hr,
      // The DB hardship override replaces the normal means rate before the
      // split is applied. Keep this as a $100 display example sourced from the
      // applied estimator rate, not a second contribution rule.

      clientContribution,
      governmentSubsidy: round2(100 - clientContribution),
      needsAssessedRate: false,
      hardshipApplied: true,
    };
    return {
      isSliding: input.tier != null && SLIDING_TIERS.has(input.tier),
      clinical,
      independence: overridden,
      everydayLiving: overridden,
      hardship: {
        applied: true,
        reductionKind: activeHardship.reductionKind,
        ratePercent,
      },
    };
  }

  return {
    isSliding: input.tier != null && SLIDING_TIERS.has(input.tier),
    clinical,
    independence: categoryEstimate(
      input.tier,
      'INDEPENDENCE',
      input.assessedIndependence,
      input.isGrandfathered,
      input.asOfDate,
    ),
    everydayLiving: categoryEstimate(
      input.tier,
      'EVERYDAY_LIVING',
      input.assessedEverydayLiving,
      input.isGrandfathered,
      input.asOfDate,
    ),
    hardship: null,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Per-service line contribution projection (SSOT for the "$ you pay" display)
// ─────────────────────────────────────────────────────────────────────────
//
// `projectLineContribution` is the single UI-layer resolver for what a
// participant pays on ONE SAH service line. It reproduces the precedence of the
// authoritative posted path `apply_sah_service_financials` (live SECURITY
// DEFINER RPC) and the projection engine `calculateSahContribution`, so the
// number a coordinator/client sees equals what actually bills — MODULO the two
// caps (lifetime + grandfathered fortnightly no-worse-off) which are applied at
// period/post time, NOT per line. Every projection is therefore an UPPER BOUND
// and MUST be labelled "Est." at the surface.
//
// It exists because the previous naive aggregate (`contributionPercentage ×
// grossCost`) IGNORED clinical=0%, hardship (Fee Reduction), pooled care
// management (CM), deemed-waive, and the caps — over-stating what
// hardship/cap/clinical participants pay (Aged Care Act 2024 Ch 4 ss 400-430
// contribution-display integrity).

/** Service types that are always clinical (0% contribution) — mirrors the live
 * SAH-CLINICAL-CATEGORY-GUARD in apply_sah_service_financials. Short synonyms
 * are accepted alongside the canonical DB values. */
const CLINICAL_SERVICE_TYPES = new Set<string>([
  'nursing',
  'allied_health',
  'podiatry',
  'physiotherapy',
  'physio',
  'occupational_therapy',
  'ot',
  'speech_pathology',
  'speech',
  'dietetics',
  'psychology',
]);

/** Personal-care service identifiers that become clinical (0%) on/after
 * 1 Oct 2026 (BLK-M1 / Aged Care Rules 2025) — mirrors the live RPC list. */
const PERSONAL_CARE_TYPES = new Set<string>([
  'personal_care',
  'assistance_self_care_activities_daily_living',
  'assistance_self_administration_medication',
  'continence_management_non_clinical',
]);

/** The date personal care becomes fully funded (0%) for all participants. */
const PERSONAL_CARE_CLINICAL_FROM = '2026-10-01';

export type SahLineMeansContext = {
  tier: SahMeansTier;
  isGrandfathered: boolean;
  assessedIndependence?: number | null;
  assessedEverydayLiving?: number | null;
};

/** Which rule set determined the effective rate — useful for display + tests. */
export type LineContributionSource =
  | 'clinical'
  | 'deemed_waive_full_cost'
  | 'deemed_waive_contribution'
  | 'pooled_cm'
  | 'hardship'
  | 'stored_inclusion'
  | 'means_tier'
  | 'needs_contribution_basis';

export type ProjectLineContributionInput = {
  /** Unit cost per delivered/planned unit. */
  unitCost?: number | string | null;
  /** Quantity — delivered quantity, or (for a planned inclusion) frequency/period. */
  quantity?: number | string | null;
  /** Fallback gross when unitCost × quantity is not derivable (e.g. delivery billable_amount). */
  billableAmount?: number | string | null;
  /** Stored/plan-spine category (CLINICAL_CARE / INDEPENDENCE / EVERYDAY_LIVING or legacy lowercase). */
  serviceCategory?: string | null;
  /** Service type (nursing / allied_health / personal_care / …) — drives clinical + personal-care gates. */
  serviceType?: string | null;
  /** Plan-spine service code (e.g. PERSONAL_CARE). */
  serviceCode?: string | null;
  /** SAH service-type token (used by the live personal-care detection). */
  sahServiceType?: string | null;
  /** Funding source code — 'CM' = pooled care management (0% contribution). */
  fundingSourceCode?: string | null;
  /** Delivery date as a tenant-tz date-only string (YYYY-MM-DD) — gates the personal-care-from-2026-10-01 rule
   * for a DELIVERED or dated line. A line with no date at all is priced under the current rule. */
  deliveryDate?: string | null;
  /** Date-effective contribution caps; defaults to deliveryDate for dated lines. */
  asOfDate?: string | null;
  /** Delivery-time waive flags (never set on planned inclusions). */
  deemedWaiveContribution?: boolean | null;
  deemedWaiveFullCost?: boolean | null;
  /** Stored inclusion contribution percentage (0..100). Mirrors the live COALESCE first operand. */
  storedContributionPercent?: number | null;
  /** Participant means context — used for the means-tier fallback via calculateSahContribution. */
  means?: SahLineMeansContext | null;
  /** Active SA hardship (Fee Reduction) determination — overrides means for non-clinical lines. */
  activeHardship?: SahActiveHardship | null;
};

export type LineContributionProjection = {
  /** Gross service cost (unitCost × quantity, or billableAmount) — the un-waived amount. */
  grossCost: number;
  /** Effective contribution rate (0..1) applied to the cost, or null when the basis is unresolvable. */
  effectiveRate: number | null;
  /** Dollars the participant pays (rounded 2dp), or null when unresolvable. Upper bound (pre-cap). */
  clientContribution: number | null;
  /** Dollars the government subsidises (cost − clientContribution), or null when unresolvable. */
  governmentSubsidy: number | null;
  /** Resolved canonical category (CLINICAL / INDEPENDENCE / EVERYDAY_LIVING) or null. */
  category: SahServiceCategory | null;
  /** Which rule set produced the rate. */
  source: LineContributionSource;
  /** Always true — this is a projection, not a posted contribution. */
  isEstimate: true;
  /** Human-readable caveats (lifetime cap; grandfathered fortnightly no-worse-off cap). */
  caveats: string[];
};

function num(value: number | string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Bucket a raw service_category value into a canonical category, mirroring the
 * no-inclusion path of apply_sah_service_financials. Falls back to the shared
 * mapper first (handles CLINICAL_CARE / INDEPENDENCE / EVERYDAY_LIVING + legacy). */
function resolveCanonicalCategory(raw: string | null | undefined): SahServiceCategory | null {
  const mapped = mapDbServiceCategoryToCanonical(String(raw ?? ''));
  if (mapped) return mapped;
  const token = String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (!token) return null;
  if (
    ['independence', 'personal_care', 'assistive_technology', 'home_modification'].includes(token)
  )
    return 'INDEPENDENCE';
  if (
    [
      'domestic',
      'domestic_assistance',
      'meals',
      'cleaning',
      'transport',
      'social_support',
      'everyday_living',
    ].includes(token)
  )
    return 'EVERYDAY_LIVING';
  if (['clinical', 'clinical_care', 'nursing', 'allied_health'].includes(token)) return 'CLINICAL';
  return null;
}

/** The later of two tenant-tz date-only strings; null when both are absent.
 * Use it to price a planned line: `laterSahDate(line.startDate, today)`. */
export function laterSahDate(
  a: string | null | undefined,
  b: string | null | undefined,
): string | null {
  const first = a ? String(a).slice(0, 10) : null;
  const second = b ? String(b).slice(0, 10) : null;
  if (!first) return second;
  if (!second) return first;
  return first >= second ? first : second;
}

/** Resolve the canonical category with the live force-clinical overrides layered
 * on: personal-care-from-2026-10-01, clinical service types, and CLINICAL%
 * funding source. */
export function resolveSahLineCategory(
  input: ProjectLineContributionInput,
): SahServiceCategory | null {
  let category = resolveCanonicalCategory(input.serviceCategory);

  const serviceType = String(input.serviceType ?? '')
    .trim()
    .toLowerCase();
  const serviceCode = String(input.serviceCode ?? '')
    .trim()
    .toUpperCase();
  const sahServiceType = String(input.sahServiceType ?? '')
    .trim()
    .toLowerCase();

  const isPersonalCare =
    serviceCode === 'PERSONAL_CARE' ||
    PERSONAL_CARE_TYPES.has(sahServiceType) ||
    PERSONAL_CARE_TYPES.has(serviceType);

  // BLK-M1: personal care is fully funded (0%) on/after 1 Oct 2026, anchored to
  // the date the line is priced for (delivery date, else the as-of date).
  // SAH-FIN-08: the rule must never be skipped for want of a date. A line with
  // no date at all is an estimate of future deliveries; the rule commenced on
  // 1 Oct 2026 and has no end date, so it applies. Callers pricing a planned
  // line must pass the projection date (today in the provider's timezone, or the
  // line's start when that is later), never a stale plan start date.
  const categoryDate = String(input.deliveryDate ?? input.asOfDate ?? '').slice(0, 10) || null;
  if (isPersonalCare && (categoryDate === null || categoryDate >= PERSONAL_CARE_CLINICAL_FROM)) {
    category = 'CLINICAL';
  }

  // Clinical-category guard: clinical service types + CLINICAL% funding → 0%.
  if (
    CLINICAL_SERVICE_TYPES.has(serviceType) ||
    String(input.fundingSourceCode ?? '')
      .trim()
      .toUpperCase()
      .startsWith('CLINICAL')
  ) {
    category = 'CLINICAL';
  }

  return category;
}

/**
 * Project the participant contribution for a single SAH service line.
 *
 * Precedence (faithful to `apply_sah_service_financials`):
 *   a. grossCost = unitCost × quantity (fallback billableAmount).
 *   b. resolve category (+ force-clinical for clinical service types / personal
 *      care on/after 2026-10-01 / CLINICAL% funding).
 *   c. effectiveRate:
 *        0 when clinical OR deemed_waive_contribution OR pooled CM (funding='CM');
 *        else active hardship (FULL_WAIVER → 0, else reduced %);
 *        else the stored inclusion % (live COALESCE first operand);
 *        else the means-tier rate via calculateSahContribution (grandfathered vs
 *          standard fixed/sliding + the client's STORED assessed rate for the
 *          sliding mid-band — reused, never re-derived);
 *        else null (needs contribution basis).
 *   d. cost = deemed_waive_full_cost ? 0 : grossCost.
 *   e. clientContribution = exact round(cost × round(percent / 100, 6), 2), ties away from zero; governmentSubsidy = cost − clientContribution.
 *
 * NOTE: The two caps (lifetime; grandfathered fortnightly no-worse-off) apply at
 * period/post time, NOT per line — so this is an UPPER BOUND. Surfaces MUST label
 * the figure "Est.".
 */
export function projectLineContribution(
  input: ProjectLineContributionInput,
): LineContributionProjection {
  const caveats: string[] = [];

  // a. gross cost
  const unit = num(input.unitCost);
  const qty = num(input.quantity);
  let grossCost: number;
  if (unit != null && unit >= 0 && qty != null && qty > 0) {
    grossCost = round2(unit * qty);
  } else {
    grossCost = Math.max(0, num(input.billableAmount) ?? 0);
  }

  // b. category
  const category = resolveSahLineCategory(input);

  const isPooledCm =
    String(input.fundingSourceCode ?? '')
      .trim()
      .toUpperCase() === 'CM';
  const isClinical = category === 'CLINICAL';

  // c. effective rate + source
  let effectiveRate: number | null;
  let source: LineContributionSource;

  if (isClinical) {
    effectiveRate = 0;
    source = 'clinical';
  } else if (input.deemedWaiveContribution) {
    effectiveRate = 0;
    source = 'deemed_waive_contribution';
  } else if (isPooledCm) {
    effectiveRate = 0;
    source = 'pooled_cm';
  } else {
    const hr = hardshipRate(
      sahHardshipForDate(input.activeHardship, input.deliveryDate ?? input.asOfDate),
    );
    if (hr !== null) {
      effectiveRate = hr;
      source = 'hardship';
    } else {
      const stored = num(input.storedContributionPercent);
      if (stored != null && stored >= 0) {
        effectiveRate = stored / 100;
        source = 'stored_inclusion';
      } else if (input.means && category) {
        try {
          const assessed =
            category === 'INDEPENDENCE'
              ? input.means.assessedIndependence
              : category === 'EVERYDAY_LIVING'
                ? input.means.assessedEverydayLiving
                : undefined;
          const breakdown = calculateSahContribution({
            serviceCategory: category,
            serviceCost: 100,
            meansTier: input.means.tier,
            individualAssessedRate: SLIDING_TIERS.has(input.means.tier)
              ? (assessed ?? undefined)
              : undefined,
            isGrandfathered: input.means.isGrandfathered,
            asOfDate: input.asOfDate ?? input.deliveryDate,
          });
          effectiveRate = breakdown.contributionRate;
          source = 'means_tier';
        } catch {
          // Sliding tier without an assessed rate — refuse to guess.
          effectiveRate = null;
          source = 'needs_contribution_basis';
        }
      } else {
        effectiveRate = null;
        source = 'needs_contribution_basis';
      }
    }
  }

  // d. cost (full-cost waive drops the whole amount)
  const fullCostWaived = Boolean(input.deemedWaiveFullCost);
  const cost = fullCostWaived ? 0 : grossCost;

  // e. amounts
  let clientContribution: number | null;
  let governmentSubsidy: number | null;
  if (effectiveRate == null) {
    clientContribution = null;
    governmentSubsidy = null;
  } else {
    clientContribution =
      source === 'hardship'
        ? roundSahParticipantContribution(
            cost,
            input.activeHardship?.reductionKind === 'FULL_WAIVER'
              ? 0
              : input.activeHardship!.reducedContributionPercent!,
          )
        : source === 'stored_inclusion'
          ? roundSahParticipantContribution(cost, input.storedContributionPercent!)
          : roundSahParticipantContribution(cost, effectiveRate, 'fraction');
    // eslint-disable-next-line statura/no-floating-point-money -- government subsidy = cost − contribution, round2-scaled; mirrors apply_sah_service_financials
    governmentSubsidy = round2(cost - clientContribution);
  }

  if (fullCostWaived) {
    // Full-cost waive dominates the display reason even if a rate resolved.
    source = 'deemed_waive_full_cost';
  }

  // caveats: caps apply at period/post time, not per line.
  if (clientContribution != null && clientContribution > 0) {
    caveats.push('subject to lifetime cap — applied at delivery');
    if (input.means?.isGrandfathered) {
      caveats.push('subject to fortnightly no-worse-off cap');
    }
  }

  return {
    grossCost,
    effectiveRate,
    clientContribution,
    governmentSubsidy,
    category,
    source,
    isEstimate: true,
    caveats,
  };
}

const lineAud = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Format a projected line contribution as "$Y (X%)" — the "$ you pay" display,
 * always paired with the percentage. Returns a "needs basis" label when the
 * contribution is unresolvable. Callers should prefix "Est." where appropriate. */
export function formatLineContribution(projection: LineContributionProjection): string {
  if (projection.clientContribution == null || projection.effectiveRate == null) {
    return 'contribution: needs basis';
  }
  return `${lineAud.format(projection.clientContribution)} contribution (${formatSahRate(
    projection.effectiveRate,
  )})`;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
